/**
 * TenantFeaturesPanel — auto-rendered super-admin UI for the feature
 * registry. Each entry in config/feature_registry.json renders as a
 * <Switch>; toggling persists via PATCH /super-admin/tenants/<id>/features.
 *
 * Hierarchy: each registry entry carries a `parent` key, and the chain can
 * be more than one level deep (e.g. modules.agents -> modules.concierge ->
 * modules.concierge.edit_prompt). Roots have `parent: null` and render at
 * the top level; every descendant renders indented underneath its parent,
 * however deep the chain goes — rendering was previously hardcoded to one
 * level (root + direct children only), which silently dropped any
 * grandchild flag from the UI entirely (not disabled, not greyed —
 * genuinely never rendered). Children are visually disabled (greyed) when
 * their parent's RESOLVED value is off — toggling the parent off does
 * NOT touch the children's stored values, the disabled state is purely
 * cosmetic to mirror the backend's resolution gating.
 *
 * Adding a new flag is a backend-only change (one entry in the JSON).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { featureLabel, featureDescription } from '@/utils/featureLabels';
import { Switch, Card, Spin, message as antdMessage, Empty, Tooltip } from 'antd';
import api from '@/services/api';
import { useLanguage } from '@/hooks/useLanguage';
import authService from '@modules/auth/services/authService';
import { useAuth } from '@/hooks/useAuth';
import { CascadeControls, CascadeStatus } from './CascadeControls';

interface FeatureRegistryEntry {
  label: string;
  description?: string;
  default: boolean;
  scope: 'tenant' | 'user';
  parent?: string | null;
  /** Unset here, it follows the nearest organization above that set it. */
  inherited_by_sub_organizations?: boolean;
}

type FeatureRegistry = Record<string, FeatureRegistryEntry>;

interface TenantFeaturesPanelProps {
  tenantId: string;
  /**
   * `'rows'` (default) renders compact Switch rows, used by the legacy
   * Edit Tenant modal. `'banners'` renders the same flags as bigger
   * tile cards that match the Feature Adoption banners on the Super
   * Admin dashboard — used by the Tenant Tree tab. The data path,
   * toggle handler, and hierarchy gating are identical between the
   * two variants; only the visual shell differs.
   */
  variant?: 'rows' | 'banners';
  /** See ModelFamiliesPanel — keeps the tenant-tree badges honest. */
  onCascadeChanged?: () => void;
}

export function TenantFeaturesPanel({ tenantId, variant = 'rows', onCascadeChanged }: TenantFeaturesPanelProps) {
  const { t, language } = useLanguage();
  const { tenantId: currentUserTenantId } = useAuth();
  const [registry, setRegistry] = useState<FeatureRegistry | null>(null);
  const [overrides, setOverrides] = useState<Record<string, boolean>>({});
  const [resolved, setResolved] = useState<Record<string, boolean>>({});
  const [cascade, setCascade] = useState<CascadeStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);

  // Reload the tenant-side state (overrides + resolved + cascade badges).
  // Used on mount and after every cascade action from CascadeControls.
  const reloadTenant = useCallback(async () => {
    const tenantRes = await api.get(`/super-admin/tenants/${tenantId}/features`);
    setOverrides(tenantRes.data?.overrides || {});
    setResolved(tenantRes.data?.resolved || {});
    setCascade(tenantRes.data?.cascade || null);
  }, [tenantId]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const [registryRes, tenantRes] = await Promise.all([
          api.get('/super-admin/features/registry'),
          api.get(`/super-admin/tenants/${tenantId}/features`),
        ]);
        if (cancelled) return;
        setRegistry(registryRes.data?.registry || {});
        setOverrides(tenantRes.data?.overrides || {});
        setResolved(tenantRes.data?.resolved || {});
        setCascade(tenantRes.data?.cascade || null);
      } catch (err: any) {
        antdMessage.error(`Failed to load features: ${err?.message || err}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [tenantId]);

  // Tenant-scoped entries only, preserving registry order.
  const tenantScopedKeys = useMemo(
    () => Object.entries(registry || {}).filter(([, v]) => v.scope === 'tenant'),
    [registry]
  );

  // Group entries into roots and a children-by-parent map. Order within each
  // bucket follows the registry's declaration order.
  const { roots, childrenByParent } = useMemo(() => {
    const rs: Array<[string, FeatureRegistryEntry]> = [];
    const cmap: Record<string, Array<[string, FeatureRegistryEntry]>> = {};
    for (const entry of tenantScopedKeys) {
      const [, meta] = entry;
      const parent = meta.parent ?? null;
      if (!parent) {
        rs.push(entry);
      } else {
        if (!cmap[parent]) cmap[parent] = [];
        cmap[parent].push(entry);
      }
    }
    return { roots: rs, childrenByParent: cmap };
  }, [tenantScopedKeys]);

  // An unset flag holds its registry default — or, for a flag that follows the
  // organization tree, what the nearest organization above set, which only
  // the server's resolved map knows.
  const unsetValue = useCallback(
    (key: string, meta: FeatureRegistryEntry): boolean =>
      (meta.inherited_by_sub_organizations ? !!resolved[key] : !!meta.default),
    [resolved],
  );

  // Local view of the resolved map. We compute this from `overrides` so the
  // disabled state of children flips the instant a parent toggle saves
  // (without waiting for the resolved map to round-trip).
  const localResolved = useMemo(() => {
    const out: Record<string, boolean> = {};
    for (const [key, meta] of tenantScopedKeys) {
      if (key in overrides) out[key] = !!overrides[key];
      else out[key] = unsetValue(key, meta);
    }
    // Walk parents → children: if a parent (or ancestor) is off, force child off.
    for (const [key, meta] of tenantScopedKeys) {
      let cursor = meta.parent ?? null;
      while (cursor) {
        if (out[cursor] === false) { out[key] = false; break; }
        const parentEntry = registry?.[cursor];
        cursor = parentEntry?.parent ?? null;
      }
    }
    return out;
  }, [overrides, tenantScopedKeys, registry, unsetValue]);

  async function toggle(key: string, value: boolean) {
    setSaving(key);
    const next = { ...overrides, [key]: value };
    try {
      const res = await api.patch(`/super-admin/tenants/${tenantId}/features`, { features: next });
      setOverrides(res.data?.overrides || next);
      setResolved(res.data?.resolved || resolved);
      // Hierarchy cascade bookkeeping: a direct edit may break an ancestor's
      // enforcement; an edit on an ENFORCING tenant pushes to its subtree.
      const cleared: string[] = res.data?.cleared_ancestor_enforcement || [];
      const pushed: number = res.data?.descendants_updated || 0;
      if (cleared.length > 0) {
        antdMessage.warning(
          t('superAdmin.cascade.editClearedAncestors') ||
          t('superAdmin.nowDiffersFromParent')
        );
      }
      if (pushed > 0) {
        antdMessage.info(
          (t('superAdmin.cascade.editPushed') || 'Change pushed to {count} sub-tenant(s) (enforcement is on).')
            .replace('{count}', String(pushed))
        );
      }
      if (cleared.length > 0 || pushed > 0) {
        reloadTenant().catch(() => { /* badges refresh on next visit */ });
      }
      onCascadeChanged?.();
      // If the super-admin just toggled a feature for THEIR OWN tenant, the
      // feature change affects what they see in the rest of the app right
      // now (sidebar gating, like/dislike buttons, etc.). Their cached
      // user.features map in localStorage was set at login time and won't
      // pick up changes until the next /auth/verify roundtrip — so trigger
      // one explicitly. authService.verifyToken() refreshes the cached user
      // and emits AUTH_CHANGE_EVENT, which makes every useFeature() reader
      // re-read.
      if (tenantId === currentUserTenantId) {
        // force: this is the refresh, not a liveness check — a cached
        // answer would defeat the entire point of calling it here.
        authService.verifyToken(true).catch(() => {
          // Network blip — the user can refresh manually. Silent failure
          // here is preferable to a confusing error toast on a successful
          // toggle.
        });
      }
      // Use the human-readable label in the toast — never the raw flag key.
      const label = featureLabel(language, key, registry?.[key]?.label);
      antdMessage.success(t('superAdmin.features.toggleSuccess', {
        key: label,
        state: value ? t('superAdmin.features.stateOn') : t('superAdmin.features.stateOff'),
      }));
    } catch (err: any) {
      // Surface the backend's error message verbatim — the hierarchy
      // validator returns the human "Cannot enable X: requires Y" string.
      const apiError = err?.response?.data?.error;
      antdMessage.error(apiError || `Save failed: ${err?.message || err}`);
    } finally {
      setSaving(null);
    }
  }

  if (loading) return <Spin />;
  if (!registry || tenantScopedKeys.length === 0) {
    return <Empty description={t('superAdmin.features.emptyDescription')} />;
  }

  // Renders a single feature row. `indent` controls the left padding so
  // children sit under their parent. `parentResolved` lets us disable the
  // child switch when the parent is off (resolved=false).
  function renderRow(key: string, meta: FeatureRegistryEntry, indent: number) {
    const isOverridden = key in overrides;
    const storedValue = isOverridden ? overrides[key] : unsetValue(key, meta);
    const parentKey = meta.parent ?? null;
    const parentResolvedTrue = parentKey ? !!localResolved[parentKey] : true;
    const disabled = !parentResolvedTrue;
    // The switch reflects the EFFECTIVE/resolved value: when the parent
    // is off we render the switch as off (greyed), even if the stored
    // value is true. The stored value isn't lost — it's preserved in the
    // DB and surfaces again the moment the parent flips back on.
    const checked = disabled ? false : !!storedValue;
    const parentLabel = parentKey ? (registry?.[parentKey]?.label ?? parentKey) : '';

    const switchEl = (
      <Switch
        checked={checked}
        loading={saving === key}
        disabled={disabled}
        onChange={(v) => toggle(key, v)}
      />
    );

    return (
      <div
        key={key}
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 12,
          paddingInlineStart: indent,
        }}
      >
        {disabled ? (
          <Tooltip title={t('superAdmin.features.requiresParent', { parent: parentLabel })}>
            <span>{switchEl}</span>
          </Tooltip>
        ) : (
          switchEl
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
            {/* Human label — the only string a non-engineer should
                need to recognize a feature by. The raw flag key is
                intentionally not rendered anywhere in this panel. */}
            <span style={{ fontWeight: 500, opacity: disabled ? 0.55 : 1 }}>{featureLabel(language, key, meta.label)}</span>
            {!isOverridden && (
              <span style={{ fontSize: 11, color: 'var(--text-tertiary, #94a3b8)' }}>
                {t(meta.inherited_by_sub_organizations
                  ? 'superAdmin.features.unsetInherited'
                  : 'superAdmin.features.unsetUsingDefault')}
              </span>
            )}
          </div>
          {meta.description && (
            <div style={{ fontSize: 12, opacity: disabled ? 0.5 : 0.75, marginTop: 4 }} dir="auto">
              {featureDescription(language, key, meta.description)}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Banner variant renders each flag as a tile that mirrors the
  // Feature Adoption banners on the Super Admin dashboard. Same toggle
  // handler, same hierarchy gating, different shell.
  function renderBanner(key: string, meta: FeatureRegistryEntry) {
    const isOverridden = key in overrides;
    const storedValue = isOverridden ? overrides[key] : unsetValue(key, meta);
    const parentKey = meta.parent ?? null;
    const parentResolvedTrue = parentKey ? !!localResolved[parentKey] : true;
    const disabled = !parentResolvedTrue;
    const checked = disabled ? false : !!storedValue;
    const parentLabel = parentKey ? (registry?.[parentKey]?.label ?? parentKey) : '';

    const switchEl = (
      <Switch
        checked={checked}
        loading={saving === key}
        disabled={disabled}
        onChange={(v) => toggle(key, v)}
      />
    );

    return (
      <div
        key={key}
        className={`feature-banner feature-banner--toggle ${checked ? 'feature-banner--on' : 'feature-banner--off'} ${parentKey ? 'feature-banner--child' : ''}`}
      >
        <div className="feature-banner-head">
          <span className={`feature-banner-pill ${checked ? 'feature-banner-pill--on' : 'feature-banner-pill--off'}`}>
            {disabled ? t('superAdmin.offParentOff') : (checked ? 'ON' : 'OFF')}
          </span>
          {disabled ? (
            <Tooltip title={t('superAdmin.features.requiresParent', { parent: parentLabel })}>
              <span>{switchEl}</span>
            </Tooltip>
          ) : (
            switchEl
          )}
        </div>
        <div className="feature-banner-label" style={{ opacity: disabled ? 0.6 : 1 }} dir="auto">
          {featureLabel(language, key, meta.label)}
        </div>
        {meta.description && (
          <div className="feature-banner-desc bidi-clamp" style={{ opacity: disabled ? 0.5 : 0.75 }} dir="auto">
            {featureDescription(language, key, meta.description)}
          </div>
        )}
        {!isOverridden && (
          <div className="feature-banner-meta">
            {t(meta.inherited_by_sub_organizations
                  ? 'superAdmin.features.unsetInherited'
                  : 'superAdmin.features.unsetUsingDefault')}
          </div>
        )}
      </div>
    );
  }

  // Recursively render a key's whole descendant subtree, not just its
  // direct children — a chain like modules.agents -> modules.concierge ->
  // modules.concierge.edit_prompt is two levels of children deep.
  function renderRowSubtree(key: string, meta: FeatureRegistryEntry, indent: number) {
    return (
      <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {renderRow(key, meta, indent)}
        {(childrenByParent[key] || []).map(([childKey, childMeta]) =>
          renderRowSubtree(childKey, childMeta, indent + 20),
        )}
      </div>
    );
  }

  function renderBannerSubtree(key: string, meta: FeatureRegistryEntry) {
    const children = childrenByParent[key] || [];
    return (
      <section key={key} className="feature-group">
        <div className="feature-group-parent">
          {renderBanner(key, meta)}
        </div>
        {children.length > 0 && (
          <div className="feature-group-children">
            {children.map(([childKey, childMeta]) =>
              renderBannerSubtree(childKey, childMeta),
            )}
          </div>
        )}
      </section>
    );
  }

  if (variant === 'banners') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Modules cascade toolbar — apply-to-descendants + enforce, the
            'modules' twin of the AI Models panel's controls. */}
        <CascadeControls tenantId={tenantId} domain="modules" cascade={cascade} onChanged={() => { reloadTenant().catch(() => {}); onCascadeChanged?.(); }} />
        <div className="feature-groups">
          {roots.map(([rootKey, rootMeta]) => renderBannerSubtree(rootKey, rootMeta))}
        </div>
      </div>
    );
  }

  return (
    <Card size="small" title={t('superAdmin.features.cardTitle')} style={{ marginTop: 16 }}>
      <div style={{ marginBottom: 14 }}>
        <CascadeControls tenantId={tenantId} domain="modules" cascade={cascade} onChanged={() => { reloadTenant().catch(() => {}); onCascadeChanged?.(); }} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {roots.map(([rootKey, rootMeta]) => renderRowSubtree(rootKey, rootMeta, 0))}
      </div>
    </Card>
  );
}
